import React, { useState, useEffect } from 'react';

const Homepage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [currentModule, setCurrentModule] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const moduleInterval = setInterval(() => {
      setCurrentModule((prev) => (prev + 1) % modules.length);
    }, 4000);
    
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4500);
    
    return () => {
      clearInterval(moduleInterval);
      clearInterval(testimonialInterval);
    };
  }, []);

  const modules = [
    {
      id: 'detective',
      title: 'Campaign Detective',
      subtitle: 'AI Campaign Forensics',
      description: 'AI-powered forensic analysis identifies exactly what\'s killing your campaign performance. Detects budget waste in real-time with 99.2% accuracy by analyzing spend patterns, audience overlap, and performance metrics.',
      icon: '🔬',
      metrics: ['$2M+ waste detected', '99.2% accuracy', 'Real-time analysis'],
      gradient: 'from-red-500 to-orange-500'
    },
    {
      id: 'waste-hunter',
      title: 'Waste Hunter',
      subtitle: 'Cross-Campaign Waste Detection',
      description: 'AI-powered waste detection system that analyzes patterns across all your campaigns. Identifies audience overlap, budget leaks, and optimization opportunities with predictive alerts.',
      icon: '🎯',
      metrics: ['Cross-campaign analysis', 'Predictive alerts', 'Auto-fix suggestions'],
      gradient: 'from-orange-500 to-red-600'
    },
    {
      id: 'deep-analysis',
      title: 'Deep Campaign Analysis',
      subtitle: 'Strategic Intelligence Engine',
      description: 'AI-powered deep dive into campaign performance with competitive analysis, audience psychology, risk assessment, and 90-day strategic roadmaps with ROI predictions.',
      icon: '📊',
      metrics: ['Competitive intel', 'Audience psychology', '90-day roadmaps'],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 'aura',
      title: 'Aura AI Assistant',
      subtitle: 'Conversational Marketing Intelligence',
      description: 'Chat with Aura, your AI marketing co-pilot. Get instant answers about campaign performance, strategic recommendations, and market insights through natural conversation.',
      icon: '💬',
      metrics: ['Real-time chat', 'Market insights', 'Strategic guidance'],
      gradient: 'from-pink-500 to-purple-600'
    },
    {
      id: 'platform',
      title: 'Platform Intelligence',
      subtitle: 'Global Platform Optimizer',
      description: 'AI recommends optimal platform mix across 195+ countries with budget allocation, timing strategies, and multi-currency support (180+ currencies). Discovers emerging platforms dynamically.',
      icon: '🌐',
      metrics: ['195+ countries', '180+ currencies', 'Dynamic discovery'],
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'market',
      title: 'Global Market Intelligence',
      subtitle: 'Strategic Market Analysis',
      description: 'Real-time market analysis across 50+ industries in 195+ countries. Competitive intelligence, opportunity mapping, regulatory insights, and market entry strategies.',
      icon: '🌍',
      metrics: ['50+ industries', 'Real-time data', 'Market entry plans'],
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      id: 'content',
      title: 'Content Generation Engine',
      subtitle: 'Psychological Copywriting AI',
      description: 'AI copywriter trained on 10+ proven frameworks (Cialdini, Schwartz, Sugarman) generates high-converting copy for 30+ content types. Analyzes and improves existing content with psychological triggers.',
      icon: '✍️',
      metrics: ['10+ frameworks', '30+ content types', 'Psychology-driven'],
      gradient: 'from-amber-500 to-yellow-500'
    },
    {
      id: 'decisions',
      title: 'Decision Tracking System',
      subtitle: 'Marketing Accountability Engine',
      description: 'Log every marketing decision and track real outcomes vs predictions with AI accountability scoring. Get strategic insights with Google Search grounding, measure ROI across 8 objective types, and build institutional knowledge from what works and what doesn\'t.',
      icon: '📈',
      metrics: ['AI accountability scoring', 'Outcome vs prediction', 'Strategic AI insights'],
      gradient: 'from-indigo-500 to-purple-500'
    }
  ];

  const testimonials = [
    {
      text: "Campaign Detective saved us $2.1M in wasted ad spend in our first quarter. The forensic analysis showed us exactly where money was bleeding - something our agency never caught.",
      author: "Sarah Chen",
      role: "VP Marketing",
      company: "TechFlow (Series B)",
      avatar: "👩‍💼",
      result: "$2.1M saved in Q1"
    },
    {
      text: "The Global Market Intelligence helped us enter 4 new countries with 92% success rate. The AI predicted competitive landscapes we would have missed entirely.",
      author: "Marcus Rodriguez", 
      role: "Head of Growth",
      company: "ScaleUp Inc",
      avatar: "👨‍🚀",
      result: "4 successful market entries"
    },
    {
      text: "Content Generation Engine increased our conversion rates by 340%. The psychological framework integration is unlike anything else on the market.",
      author: "Emma Thompson",
      role: "CMO",
      company: "Enterprise Corp",
      avatar: "👩‍💻",
      result: "340% conversion improvement"
    },
    {
      text: "Decision Tracking changed how our team operates. We now have complete accountability and can prove ROI on every marketing decision we make.",
      author: "James Park",
      role: "Marketing Director",
      company: "Growth Ventures",
      avatar: "👨‍💼",
      result: "Complete team accountability"
    }
  ];

  const useCases = [
    {
      persona: "Enterprise Marketing Teams",
      icon: "🏢",
      challenge: "Managing complex multi-platform campaigns across global markets with accountability",
      solution: "8 integrated AI modules: Campaign forensics, waste detection, deep analysis, conversational AI, platform optimization, market intelligence, content generation, and decision tracking with auto-detection",
      outcome: "Complete marketing intelligence with predictive failure prevention and full decision accountability"
    },
    {
      persona: "High-Growth Startups", 
      icon: "🚀",
      challenge: "Scaling marketing efficiently while entering new markets without wasting budget",
      solution: "AI-powered market entry planning across 195 countries with platform intelligence, budget optimization, and decision tracking to learn from every move",
      outcome: "Faster, smarter market expansion with 92% success rate vs 23% industry average"
    },
    {
      persona: "Marketing Agencies",
      icon: "🤝",
      challenge: "Proving ROI to clients and preventing campaign failures that damage relationships",
      solution: "Real-time campaign forensics, decision tracking with accountability scores, outcome analysis, and client reporting with PDF exports",
      outcome: "Higher client retention with measurable results, complete transparency, and data-driven accountability"
    },
    {
      persona: "Performance Marketers",
      icon: "📈",
      challenge: "Optimizing campaigns across platforms while creating high-converting content",
      solution: "Platform intelligence + content generation engine with psychological frameworks + decision tracking to measure what actually works",
      outcome: "340% average conversion improvement with AI-optimized creative, targeting, and measurable decision outcomes"
    }
  ];

  const stats = [
    { number: '$2M+', label: 'Ad Waste Detected', icon: '💰' },
    { number: '195', label: 'Countries Supported', icon: '🌍' },
    { number: '180+', label: 'Currencies', icon: '💱' },
    { number: '99.2%', label: 'AI Accuracy', icon: '🤖' }
  ];

  const technicalFeatures = [
    {
      title: 'Real-Time Market Intelligence',
      description: 'Live market data integration for current insights, trends, and competitive analysis with Google Search grounding',
      icon: '🔍'
    },
    {
      title: 'Multi-Currency Intelligence',
      description: '180+ currencies with localized cost analysis and ROI calculations across global markets',
      icon: '💱'
    },
    {
      title: 'Psychological Framework Library',
      description: '10+ proven copywriting frameworks (Cialdini, Schwartz, Sugarman, Halbert) for high-converting content',
      icon: '🧠'
    },
    {
      title: 'Decision Accountability Engine',
      description: 'Log marketing decisions, track outcomes vs predictions with AI accountability scoring, and build institutional knowledge across 8 objective types',
      icon: '📊'
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 py-2 transition-all duration-300 ${
        scrollY > 50 ? 'bg-black/95 backdrop-blur-xl border-b border-orange-400/20' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8">
          <div className="flex items-center justify-between h-14 gap-4">
            <div className="flex items-center cursor-pointer hover:scale-105 transition-transform">
              <span className="text-xl sm:text-2xl font-black text-orange-400 tracking-tight">WhyDidItDie</span>
            </div>

            <div className="hidden lg:flex items-center flex-1 justify-center gap-5 xl:gap-8">
              <a href="#modules" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">8 AI Modules</a>
              <a href="https://app.whydiditdie.com/features" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">Features</a>
              <a href="https://app.whydiditdie.com/pricing" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">Pricing</a>
              <a href="#demo" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">Demo</a>
              <a href="https://app.whydiditdie.com/integrations" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">Integrations</a>
              <a href="https://app.whydiditdie.com/subscribe" className="text-white/90 hover:text-orange-400 font-medium text-sm transition-all hover:-translate-y-0.5">📧 Subscribe</a>
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <a href="https://app.whydiditdie.com/login" className="px-4 py-2 border border-white/20 rounded-lg font-semibold text-sm hover:bg-white/10 hover:border-white/40 transition-all hover:-translate-y-0.5">
                Login
              </a>
              <a href="https://app.whydiditdie.com/signup" className="px-4 py-2 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg font-bold text-sm shadow-lg shadow-orange-500/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/60 transition-all">
                Start Free Trial
              </a>
            </div>

            <button 
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 border border-white/30 text-white hover:bg-white/20 transition-all"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden fixed top-[68px] left-0 right-0 bottom-0 bg-black/97 backdrop-blur-xl p-6 overflow-y-auto">
            <div className="space-y-1">
              <a href="#modules" className="block py-4 text-lg font-semibold border-b border-white/10 hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>8 AI Modules</a>
              <a href="https://app.whydiditdie.com/features" className="block py-4 text-lg font-semibold border-b border-white/10 hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="https://app.whydiditdie.com/pricing" className="block py-4 text-lg font-semibold border-b border-white/10 hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>Pricing</a>
              <a href="#demo" className="block py-4 text-lg font-semibold border-b border-white/10 hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>Demo</a>
              <a href="https://app.whydiditdie.com/integrations" className="block py-4 text-lg font-semibold border-b border-white/10 hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>Integrations</a>
              <a href="https://app.whydiditdie.com/subscribe" className="block py-4 text-lg font-semibold hover:text-orange-400 hover:pl-3 transition-all" onClick={() => setIsMenuOpen(false)}>📧 Subscribe</a>
            </div>
            <div className="flex flex-col gap-4 mt-8 pt-6 border-t border-white/15">
              <a href="https://app.whydiditdie.com/login" className="py-4 border-2 border-white/40 rounded-xl font-semibold text-center hover:bg-white/10 transition-all" onClick={() => setIsMenuOpen(false)}>
                Login
              </a>
              <a href="https://app.whydiditdie.com/signup" className="py-4 bg-gradient-to-r from-orange-400 to-red-500 rounded-xl font-bold text-center shadow-lg shadow-orange-500/40 hover:-translate-y-0.5 hover:shadow-xl transition-all" onClick={() => setIsMenuOpen(false)}>
                Start Free Trial
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center relative overflow-hidden pt-20 pb-10">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-400/15 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-orange-400/20 border border-orange-400/30 rounded-full px-4 py-1.5 text-xs font-semibold text-orange-300 mb-5">
                <span className="text-sm">🤖</span>
                8 AI-Powered Marketing Intelligence Modules
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black leading-tight mb-5">
                AI Marketing Intelligence Platform with
                <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent"> Campaign Forensics</span>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl leading-relaxed text-white/80 mb-7 max-w-2xl mx-auto lg:mx-0">
                The only platform with <strong className="text-orange-300 font-semibold">8 integrated AI modules</strong>: 
                Campaign Detective for forensic analysis, Waste Hunter for cross-campaign detection, Deep Analysis for strategic intelligence, 
                Aura AI Assistant for conversational guidance, Platform Intelligence across 195+ countries, Global Market Intelligence, 
                AI Content Generation with psychological frameworks, and Decision Tracking with accountability scoring.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-9 justify-center lg:justify-start">
                <a href="https://app.whydiditdie.com/signup" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-400 to-red-500 px-7 py-4 rounded-2xl font-bold text-lg shadow-xl shadow-orange-500/40 hover:-translate-y-1 hover:shadow-2xl transition-all">
                  <span>🚀</span>
                  Start Free 2-Days Trial
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </a>
                
                <button 
                  onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center justify-center gap-2 border-2 border-white/20 px-7 py-4 rounded-2xl font-semibold text-lg hover:bg-white/10 hover:border-white/40 hover:-translate-y-0.5 transition-all"
                >
                  <span>🎥</span>
                  Watch Platform Demo
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6">
                {stats.map((stat, index) => (
                  <React.Fragment key={index}>
                    <div className="flex flex-col items-center">
                      <span className="text-xl sm:text-2xl font-black text-orange-400">{stat.number}</span>
                      <span className="text-xs text-white/60 font-medium">{stat.label}</span>
                    </div>
                    {index < stats.length - 1 && <div className="w-px h-9 bg-white/20 hidden sm:block" />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="flex justify-center items-center">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5 max-w-sm w-full">
                <div className="flex items-start gap-3 mb-5">
                  <span className="text-2xl mt-0.5">🔬</span>
                  <div className="flex-1">
                    <div className="text-lg font-extrabold text-white">Campaign Detective Analysis</div>
                    <div className="text-sm text-white/75 font-medium">Black Friday 2024 Campaign</div>
                  </div>
                  <span className="text-lg">✅</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <div className="bg-black/30 rounded-xl p-4 text-center border border-white/10">
                    <span className="text-xs text-white/70 block mb-2 font-medium">Waste Detected</span>
                    <span className="text-xl font-black text-red-500">$$2M+</span>
                  </div>
                  <div className="bg-black/30 rounded-xl p-4 text-center border border-white/10">
                    <span className="text-xs text-white/70 block mb-2 font-medium">AI Confidence</span>
                    <span className="text-xl font-black text-green-500">99.2%</span>
                  </div>
                </div>
                
                <div className="bg-orange-400/15 border border-orange-400/30 rounded-xl p-4 text-sm leading-relaxed text-slate-100 mb-4">
                  💡 <strong>Forensic Finding:</strong> 73% audience overlap detected causing massive budget waste. 
                  AI recommends exclusion targeting to prevent $2M+ loss.
                </div>
                
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-2 text-center">
                    <span className="text-xs text-purple-300 block">Deep Analysis</span>
                  </div>
                  <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-2 text-center">
                    <span className="text-xs text-blue-300 block">Platform Intel</span>
                  </div>
                  <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-2 text-center">
                    <span className="text-xs text-green-300 block">Market Data</span>
                  </div>
                </div>
                
                <a 
                  href="https://app.whydiditdie.com/signup"
                  className="block w-full bg-gradient-to-r from-green-500 to-emerald-600 py-3.5 rounded-xl font-bold text-sm tracking-wide hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/40 transition-all text-center"
                >
                  Start Detecting Waste Now →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8 AI Modules Section */}
      <section id="modules" className="py-20 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              8 Integrated AI Marketing Intelligence Modules
            </h2>
            <p className="text-lg text-white/70 max-w-3xl mx-auto leading-relaxed">
              From campaign forensics to decision accountability - a complete AI ecosystem for predictive marketing intelligence, 
              strategic optimization, and global market expansion across 195+ countries with 180+ currencies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {modules.map((module, index) => (
              <div
                key={module.id}
                className={`bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 transition-all duration-300 cursor-pointer hover:-translate-y-2 hover:shadow-2xl hover:shadow-orange-500/30 hover:border-orange-400/30 ${
                  index === currentModule ? '-translate-y-2 shadow-2xl shadow-orange-500/30 border-orange-400/30' : ''
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${module.gradient} flex items-center justify-center text-2xl flex-shrink-0`}>
                    {module.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{module.title}</h3>
                    <p className="text-sm text-white/60">{module.subtitle}</p>
                  </div>
                </div>
                
                <p className="text-sm leading-relaxed text-white/80 mb-4">
                  {module.description}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {module.metrics.map((metric, i) => (
                    <span key={i} className="bg-orange-400/20 border border-orange-400/30 rounded-2xl px-3 py-1 text-xs font-semibold text-orange-300">
                      {metric}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-2.5">
            {modules.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentModule(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentModule ? 'bg-orange-400 w-8' : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-20 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Enterprise-Grade AI Technology Stack
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
              Powered by advanced AI with real-time data integration, multi-currency intelligence, 
              proven psychological frameworks, and complete decision accountability
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {technicalFeatures.map((feature, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:-translate-y-1 hover:border-orange-400/30 transition-all">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{feature.icon}</div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-sm leading-relaxed text-white/70">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Built for Modern Marketing Organizations
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
              From high-growth startups to Fortune 500 enterprises - complete marketing intelligence with 
              campaign forensics, content generation, and decision tracking
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:-translate-y-1 hover:border-orange-400/30 transition-all min-h-[300px] flex flex-col">
                <div className="text-center mb-5">
                  <div className="text-4xl mb-3">{useCase.icon}</div>
                  <h3 className="text-xl font-bold text-white">{useCase.persona}</h3>
                </div>
                
                <div className="flex-grow mb-4 space-y-4">
                  <div>
                    <h4 className="text-xs font-semibold text-orange-400 uppercase tracking-wider mb-1.5">Challenge</h4>
                    <p className="text-sm leading-relaxed text-white/80">{useCase.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-xs font-semibold text-orange-400 uppercase tracking-wider mb-1.5">Solution</h4>
                    <p className="text-sm leading-relaxed text-white/80">{useCase.solution}</p>
                  </div>
                </div>
                
                <div className="bg-green-500/20 border border-green-500/30 rounded-xl px-4 py-3 flex items-center gap-2 mt-auto">
                  <span className="text-base flex-shrink-0">🎯</span>
                  <span className="text-sm font-semibold text-green-400 leading-tight">{useCase.outcome}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-20 bg-gradient-to-br from-zinc-900 via-purple-900/20 to-zinc-900">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-lg">
              <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 rounded-full px-4 py-1.5 text-xs font-semibold text-red-300 mb-5">
                <span className="text-sm">🚨</span>
                Campaign Detective Live Demo
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-5 text-white">
                See Campaign Detective Optimize Your <span className="bg-gradient-to-r from-red-400 to-orange-500 bg-clip-text text-transparent">Ad Performance</span> Instantly
              </h2>
              <p className="text-base lg:text-lg leading-relaxed text-white/80 mb-7">
                Watch how Campaign Detective's AI analyzes performance metrics in real-time, 
                identifies champion campaigns, and provides actionable insights for smarter optimization.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-white/90">
                  <span className="text-lg">📊</span>
                  <span>Analyzes performance metrics across all campaigns</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-white/90">
                  <span className="text-lg">🏆</span>
                  <span>Identifies your top-performing "champion" campaigns</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-white/90">
                  <span className="text-lg">⚡</span>
                  <span>Real-time insights for smarter optimization decisions</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-white/90">
                  <span className="text-lg">🎯</span>
                  <span>Focuses attention on highest-performing opportunities</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl aspect-video relative overflow-hidden transition-all hover:scale-[1.02] hover:shadow-2xl group">
                <iframe
                  src="https://www.youtube.com/embed/mfFNp9AiZG0?rel=0&modestbranding=1&showinfo=0"
                  title="Campaign Detective Demo - AI Tool That Finds Your Ad Waste & Boosts Performance"
                  className="w-full h-full rounded-2xl"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
                
                <div className="absolute top-4 right-4 flex gap-3 z-10 pointer-events-none">
                  <div className="bg-black/60 backdrop-blur-md rounded-lg px-3 py-2 text-center">
                    <span className="block text-sm font-black text-red-400">Real-Time</span>
                    <span className="text-[10px] text-white/80">AI Analysis</span>
                  </div>
                  <div className="bg-black/60 backdrop-blur-md rounded-lg px-3 py-2 text-center">
                    <span className="block text-sm font-black text-red-400">Performance</span>
                    <span className="text-[10px] text-white/80">Optimization</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Trusted by Marketing Leaders Worldwide
            </h2>
            <p className="text-lg text-white/70">From Series A startups to Fortune 500 enterprises</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center">
              <div className="animate-fade-in">
                <div className="text-xl sm:text-2xl italic leading-relaxed text-white mb-7">
                  "{testimonials[currentTestimonial].text}"
                </div>
                
                <div className="flex items-center justify-center gap-4 mb-5">
                  <div className="text-4xl">{testimonials[currentTestimonial].avatar}</div>
                  <div className="text-left">
                    <div className="text-lg font-bold text-white">{testimonials[currentTestimonial].author}</div>
                    <div className="text-base text-orange-400">{testimonials[currentTestimonial].role}</div>
                    <div className="text-sm text-white/60">{testimonials[currentTestimonial].company}</div>
                  </div>
                </div>
                
                <div className="bg-green-500/20 border border-green-500/30 rounded-xl px-5 py-2.5 inline-flex items-center gap-2 font-semibold text-green-400">
                  🎯 Result: {testimonials[currentTestimonial].result}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-2.5 mt-7">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentTestimonial ? 'bg-orange-400 w-8' : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA Section */}
      <section className="py-20 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-4xl mx-auto px-5 sm:px-7 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-400/20 border border-orange-400/30 rounded-full px-4 py-1.5 text-xs font-semibold text-orange-300 mb-5">
            <span className="text-sm">📧</span>
            Weekly Marketing Intelligence
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4">
            Get <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">AI Marketing Insights</span> in Your Inbox
          </h2>
          
          <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
            Campaign forensics, waste detection strategies, AI intelligence updates, and the kind of 
            insights that actually move the needle. No fluff, just data-driven marketing intelligence.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <a 
              href="https://app.whydiditdie.com/subscribe" 
              className="group inline-flex items-center gap-2 bg-gradient-to-r from-orange-400 to-red-500 px-8 py-4 rounded-xl font-bold text-lg shadow-lg shadow-orange-500/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/60 transition-all"
            >
              <span>📧</span>
              Subscribe to Our Blog
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>8 AI Module Updates</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>Campaign Forensics</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>Waste Detection Tips</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>No Spam Ever</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-[-50px] left-[-50px] w-[200px] h-[200px] bg-white/10 rounded-full animate-pulse" />
          <div className="absolute bottom-[-100px] right-[-80px] w-[300px] h-[300px] bg-white/10 rounded-full animate-pulse animation-delay-2000" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-white/5 rounded-full animate-pulse animation-delay-4000" />
        </div>
        
        <div className="max-w-4xl mx-auto px-5 sm:px-7 lg:px-8 relative z-10 text-center">
          <div className="mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black mb-4 sm:mb-6 text-white drop-shadow-lg leading-tight">
              Ready to Stop Your Next Marketing Failure?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl leading-relaxed text-white/95 max-w-2xl mx-auto">
              Join thousands of marketing teams using AI intelligence to predict failures, 
              optimize performance, and achieve unprecedented ROI.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-center mb-8 sm:mb-10 lg:mb-12">
            <a href="https://app.whydiditdie.com/signup" className="group relative bg-white text-purple-600 px-6 sm:px-8 lg:px-10 py-4 sm:py-5 rounded-2xl font-extrabold text-base sm:text-lg shadow-2xl hover:-translate-y-1 hover:scale-105 transition-all duration-300 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <div className="relative flex items-center justify-center gap-2 sm:gap-3">
                <span className="text-lg sm:text-xl">🚀</span>
                <div className="text-left">
                  <div className="text-base sm:text-lg">Start Free 30-Day Trial</div>
                  <div className="text-xs opacity-80 font-medium">No credit card required</div>
                </div>
                <span className="text-lg sm:text-xl group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </a>
            
            <button className="relative bg-white/10 backdrop-blur-md text-white px-6 sm:px-8 lg:px-10 py-4 sm:py-5 rounded-2xl font-bold text-base sm:text-lg border-2 border-white/30 hover:bg-white/20 hover:border-white/60 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden">
              <div className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center justify-center gap-2">
                <span className="text-lg sm:text-xl">📞</span>
                <span>Schedule Demo Call</span>
              </div>
            </button>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-8 sm:mb-10">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-black shadow-lg shadow-green-500/30">✓</div>
              <span className="text-sm sm:text-base text-white/95">Full platform access</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-black shadow-lg shadow-green-500/30">✓</div>
              <span className="text-sm sm:text-base text-white/95">AI analysis included</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-black shadow-lg shadow-green-500/30">✓</div>
              <span className="text-sm sm:text-base text-white/95">Expert onboarding</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:inline-flex items-center gap-4 sm:gap-6 lg:gap-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl sm:rounded-3xl px-6 sm:px-8 lg:px-10 py-4 sm:py-6">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-black text-white drop-shadow">10,000+</div>
              <div className="text-[10px] sm:text-xs text-white/80 font-medium uppercase tracking-wide">Active Users</div>
            </div>
            <div className="hidden sm:block w-px h-8 sm:h-10 bg-white/30" />
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-black text-white drop-shadow">$2M+</div>
              <div className="text-[10px] sm:text-xs text-white/80 font-medium uppercase tracking-wide">Ad Spend Saved</div>
            </div>
            <div className="hidden sm:block w-px h-8 sm:h-10 bg-white/30" />
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-black text-white drop-shadow">99.2%</div>
              <div className="text-[10px] sm:text-xs text-white/80 font-medium uppercase tracking-wide">Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black pt-12 pb-6">
        <div className="max-w-7xl mx-auto px-5 sm:px-7 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8 mb-8">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl font-black text-orange-400 tracking-tight">WhyDidItDie</span>
              </div>
              <p className="text-sm leading-relaxed text-white/70 mb-5 max-w-xs">
                AI-powered marketing intelligence that predicts failures, prevents waste, and maximizes ROI across global markets.
              </p>
              <h4 className="text-base font-bold text-white mb-4">Follow Us</h4>
              <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4">
                <a
                  href="https://www.instagram.com/ai_whydiditdie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-orange-500/20 hover:border-orange-500/50 transition-all"
                >
                  <svg className="w-5 h-5 text-orange-500 group-hover:text-orange-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.326 3.608 1.301.975.975 1.24 2.242 1.301 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.326 2.633-1.301 3.608-.975.975-2.242 1.24-3.608 1.301-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.326-3.608-1.301-.975-.975-1.24-2.242-1.301-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.326-2.633 1.301-3.608.975-.975 2.242-1.24 3.608-1.301 1.266-.058 1.646-.07 4.85-.07m0-2.163c-3.259 0-3.667.014-4.947.072-1.524.069-2.978.377-4.103 1.502-1.125 1.125-1.433 2.579-1.502 4.103-.058 1.28-.072 1.688-.072 4.947s.014 3.667.072 4.947c.069 1.524.377 2.978 1.502 4.103 1.125 1.125 2.579 1.433 4.103 1.502 1.28.058 1.688.072 4.947.072s3.667-.014 4.947-.072c1.524-.069 2.978-.377 4.103-1.502 1.125-1.125 1.433-2.579 1.502-4.103.058-1.28.072-1.688.072-4.947s-.014-3.667-.072-4.947c-.069-1.524-.377-2.978-1.502-4.103-1.125-1.125-2.579-1.433-4.103-1.502-1.28-.058-1.688-.072-4.947-.072z"/>
                    <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a4 4 0 110-8 4 4 0 010 8z"/>
                    <circle cx="18.406" cy="5.594" r="1.44"/>
                  </svg>
                  <span className="text-sm text-white/90 group-hover:text-orange-400">Instagram</span>
                </a>
                
                <a
                  href="https://x.com/whydiditdie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-orange-500/20 hover:border-orange-500/50 transition-all"
                >
                  <svg className="w-5 h-5 text-orange-500 group-hover:text-orange-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zM16.62 20.61h2.048L7.353 3.39H5.154l11.466 17.22z"/>
                  </svg>
                  <span className="text-sm text-white/90 group-hover:text-orange-400">X (Twitter)</span>
                </a>
                
                <a
                  href="https://www.facebook.com/whydiditdie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-orange-500/20 hover:border-orange-500/50 transition-all"
                >
                  <svg className="w-5 h-5 text-orange-500 group-hover:text-orange-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="text-sm text-white/90 group-hover:text-orange-400">Facebook</span>
                </a>
                
                <a
                  href="https://www.tiktok.com/@app.whydiditdie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-orange-500/20 hover:border-orange-500/50 transition-all"
                >
                  <svg className="w-5 h-5 text-orange-500 group-hover:text-orange-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.04-.1z"/>
                  </svg>
                  <span className="text-sm text-white/90 group-hover:text-orange-400">TikTok</span>
                </a>
              </div>
            </div>
            
            <div className="lg:col-span-3 grid sm:grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <h4 className="text-base font-bold text-white mb-4">Product</h4>
                <div className="space-y-3">
                  <a href="https://app.whydiditdie.com/features" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Features</a>
                  <a href="https://app.whydiditdie.com/pricing" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Pricing</a>
                  <a href="https://app.whydiditdie.com/how-it-works" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">How it works</a>
                  <a href="https://app.whydiditdie.com/integrations" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Integrations</a>
                </div>
              </div>
              
              <div>
                <h4 className="text-base font-bold text-white mb-4">Company</h4>
                <div className="space-y-3">
                  <a href="https://app.whydiditdie.com/about" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">About</a>
                  <a href="https://app.whydiditdie.com/subscribe" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Blog & Newsletter</a>
                  <a href="https://app.whydiditdie.com/blog" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Blog</a>
                  <a href="https://app.whydiditdie.com/careers" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Careers</a>
                  <a href="https://app.whydiditdie.com/contact" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Contact</a>
                </div>
              </div>
              
              <div>
                <h4 className="text-base font-bold text-white mb-4">Support</h4>
                <div className="space-y-3">
                  <a href="https://app.whydiditdie.com/help" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Help Center</a>
                  <a href="https://app.whydiditdie.com/docs" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Documentation</a>
                  <a href="https://app.whydiditdie.com/status" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Status</a>
                  <a href="https://app.whydiditdie.com/community" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Community</a>
                </div>
              </div>
              
              <div>
                <h4 className="text-base font-bold text-white mb-4">Legal</h4>
                <div className="space-y-3">
                  <a href="https://app.whydiditdie.com/privacy" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Privacy</a>
                  <a href="https://app.whydiditdie.com/terms" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Terms</a>
                  <a href="https://app.whydiditdie.com/security" className="block text-sm text-white/70 hover:text-orange-400 transition-colors">Security</a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="pt-6 border-t border-white/10 text-center">
            <p className="text-sm text-white/60">
              © {new Date().getFullYear()} WhyDidItDie. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Homepage;